// /src/components/Footer.js
import React from 'react';
import { Link } from 'react-router-dom';
import { Heart } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const KRAC_URL = 'https://kracradio.com';
const DONATE_URL = 'https://www.paypal.com/donate/?hosted_button_id=GUPL4K5WR3ZG4';

const btnRed =
  'px-3 py-2 rounded-lg text-sm font-medium bg-red-600 hover:bg-red-700 text-white transition-colors';
const btnAmber =
  'px-3 py-2 rounded-lg text-sm font-medium bg-amber-500 text-black hover:bg-amber-600 transition-colors';

const Footer = () => {
  const { t, language } = useLanguage();
  const f = t.footer || {};
  const h = t.header || {};
  const currentYear = new Date().getFullYear();

  const L = {
    useful: f.usefulLinks || (language === 'en' ? 'Useful links' : language === 'es' ? 'Enlaces útiles' : 'Liens utiles'),
    information: f.information || (language === 'en' ? 'Information' : language === 'es' ? 'Información' : 'Information'),
    rights: f.rightsReserved || (language === 'en' ? 'All rights reserved.' : language === 'es' ? 'Todos los derechos reservados.' : 'Tous droits réservés.'),
    app: h.home || (language === 'en' ? 'Home' : language === 'es' ? 'Inicio' : 'Accueil'),
    about: h.about || (language === 'en' ? 'About' : language === 'es' ? 'Acerca de' : 'À propos'),
    history: h.history || (language === 'en' ? 'History' : language === 'es' ? 'Historial' : 'Historique'),
    contact: h.contact || (language === 'en' ? 'Contact' : language === 'es' ? 'Contacto' : 'Contact'),
    madeWithLove: f.madeWithLove || (language === 'en' ? 'Made with passion' : language === 'es' ? 'Hecho con pasión' : 'Fait avec passion'),
    localProcessing: f.localProcessing || (language === 'en' ? '100% local processing' : language === 'es' ? 'Procesamiento 100% local' : 'Traitement 100% local'),
    noUpload: f.noUpload || (language === 'en' ? 'No files sent to server' : language === 'es' ? 'Ningún archivo enviado al servidor' : 'Aucun fichier envoyé sur serveur'),
    openSource: f.openSource || (language === 'en' ? 'Open source and free' : language === 'es' ? 'Código abierto y gratuito' : 'Open source et gratuit'),
  };

  const desc =
    f.description ||
    t.subtitle ||
    'Éditeur de tags MP3 en ligne, gratuit et facile à utiliser. Modifiez vos métadonnées musicales directement dans votre navigateur.';

  const openExt = (url) => {
    try { window.open(url, '_blank', 'noopener,noreferrer'); }
    catch { window.location.href = url; }
  };

  return (
    <footer className="bg-gray-900 text-white py-10 px-4 mt-12">
      <div className="max-w-6xl mx-auto">
        {/* === 4 blocs, uniforme === */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-12 items-start">
          {/* Bloc 1 — Logo à GAUCHE + desc */}
          <div className="flex flex-col items-start gap-4 min-w-0">
            <img
              src="/logo.png"
              alt="Logo"
              className="h-10 w-auto rounded-md object-contain self-start"
            />
            <p className="text-gray-400 text-sm leading-relaxed">{desc}</p>
          </div>

          {/* Bloc 2 — Liens utiles */}
          <div className="flex flex-col gap-4 min-w-0">
            <h4 className="text-lg font-semibold">{L.useful}</h4>
            <ul className="space-y-2 text-gray-300">
              <li><Link to="/mp3tagedit" className="hover:text-blue-400 transition-colors text-sm">{L.app}</Link></li>
              <li><Link to="/about" className="hover:text-blue-400 transition-colors text-sm">{L.about}</Link></li>
              <li><Link to="/history" className="hover:text-blue-400 transition-colors text-sm">{L.history}</Link></li>
              <li><Link to="/contact" className="hover:text-blue-400 transition-colors text-sm">{L.contact}</Link></li>
            </ul>
          </div>

          {/* Bloc 3 — Information */}
          <div className="flex flex-col gap-4 min-w-0">
            <h4 className="text-lg font-semibold">{L.information}</h4>
            <div className="flex items-center gap-2 text-gray-300 text-sm">
              <Heart className="w-4 h-4 text-red-400" />
              {L.madeWithLove}
            </div>
            <div className="text-xs text-gray-400 space-y-1">
              <p>• {L.localProcessing}</p>
              <p>• {L.noUpload}</p>
              <p>• {L.openSource}</p>
            </div>
          </div>

          {/* Bloc 4 — Boutons */}
          <div className="flex flex-col gap-4 min-w-0 md:items-end">
            <h4 className="sr-only">Actions</h4>
            <div className="flex flex-wrap gap-2 md:justify-end">
              <button type="button" onClick={() => openExt(KRAC_URL)} className={btnRed}>
                {h.kracRadio || 'KracRadio'}
              </button>
              <button type="button" onClick={() => openExt(DONATE_URL)} className={btnAmber}>
                {h.donation || 'Faire un don'}
              </button>
            </div>
          </div>
        </div>

        {/* === Bas de page === */}
        <div className="border-t border-gray-800 mt-10 pt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center text-gray-400 text-sm">
            <p className="text-center md:text-left">
              © {currentYear} {L.rights}
            </p>
            <p className="text-center">
              <a href="https://h1site.com" target="_blank" rel="noreferrer" className="hover:text-blue-400 underline">
                Cette application est une création <strong>Agence Web H1Site</strong>
              </a>
            </p>
            <p className="text-center md:text-right">
              <span>Confidentialité</span> • <span>Conditions d’utilisation</span>
            </p>
          </div>
        </div>

        {/* Version (optionnel) */}
        <div className="text-center mt-4">
          <span className="inline-block bg-gray-800 text-gray-400 px-3 py-1 rounded-full text-xs">
            {f.version || 'Version'} 1.1.0
          </span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
